import { useEffect, type FC, type ReactElement } from "react";
import { Navigate } from "react-router-dom";

import RegulatorPortalPending from "./RegulatorPortalPending";
import { useRegulatorPortalSession } from "./useRegulatorPortalSession";
import { REGULATOR_PORTAL_SIGN_IN_ROUTE } from "./regulatorPortalRoutes";
import { clearRegulatorPortalSession } from "../../utils/tokenUtils";

type RequireRegulatorAuthProps = {
  children: ReactElement;
};

/**
 * Route guard for `/regulator-portal/*`.
 *
 * Every decision comes from the authenticated `/me` response, never from
 * decoding the stored JWT: a regulator awaiting approval is issued tokens with
 * no group claim, so claims cannot tell a pending regulator from a stranger.
 *
 *   resolving -> branded wait (polls `/me`)
 *   pending   -> holding screen (polls `/me`, flips to the portal on approval)
 *   approved  -> the portal
 *   denied    -> drop the stale session, back to regulator sign-in
 *   absent    -> drop leftover storage (expired / junk key), back to sign-in
 */
const RequireRegulatorAuth: FC<RequireRegulatorAuthProps> = ({ children }) => {
  const sessionState = useRegulatorPortalSession();

  useEffect(() => {
    // Clearing on the way out stops a leftover key from ping-ponging between
    // this guard and the sign-in page (or `RedirectIfAuthenticated` on `/`).
    if (sessionState === "denied" || sessionState === "absent") {
      clearRegulatorPortalSession();
    }
  }, [sessionState]);

  if (sessionState === "resolving") {
    return <RegulatorPortalPending variant="resolving" />;
  }

  if (sessionState === "pending") {
    return <RegulatorPortalPending />;
  }

  if (sessionState === "approved") {
    return children;
  }

  return <Navigate to={REGULATOR_PORTAL_SIGN_IN_ROUTE} replace />;
};

export default RequireRegulatorAuth;
